import { useState, useRef, useContext } from 'react'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useMutation } from 'react-query'
import { useTranslation } from 'next-i18next'
import { isEmpty } from 'lodash-es'

// Libs
import { useAppSelector } from '@/redux/hooks'
import { handleChatSend } from '@/methods/message/handleChatSend'
import { handleLoginOrRegister } from '@/methods/handleLoginOrRegister'
import { setUserRecord } from '@/api/setUserRecord'
import { handleGtmLiveInteraction } from '@/methods/handleGtmEvent'

// Components
import Icon from '@/assets/icons'
import Popup from '@/components/common/Popup'
import Alert from './components/Alert'

// Constants
import { GTM_LOCATION, GtmLiveInteractionType } from '@/constants/gtmEvent'
import { VALID_BRAND_NAME } from '@/constants/brand'

// Contexts
import { LiveRoomContext } from '@/components/pages/LiveRoom/index'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

interface ChatInputProps {
  className?: string
  isChatDisabled: boolean
  setIsBySelf: any
  isHost: boolean
  roomId?: string
  hostId?: string
}

const MAX_MESSAGE_LENGTH = 50
const SEND_BUFFER = 3 // 目前每次發言要間隔兩秒

const ChatInput = (props: ChatInputProps) => {
  const { className, isChatDisabled, setIsBySelf, isHost, hostId = '', roomId = '' } = props

  const { t } = useTranslation()

  const liveRoomContext: any = useContext(LiveRoomContext)

  const [currentValue, setCurrentValue] = useState('')
  const [canSend, setCanSend] = useState(true)
  const [isSlowing, setIsSlowing] = useState(false)

  const loginPopupRef = useRef<any>(null)

  const fixedPlayerData = useAppSelector((state) => state.common.fixedPlayerData)
  const memberData = useAppSelector((state) => state.member?.memberData)
  const socketData = useAppSelector((state) => state.socket)
  const { socket, userToken, silentUserList } = socketData
  const { nickName, headshotUrl, role, memberInfoId } = memberData

  const isMessageLimited = silentUserList?.includes(userToken)

  const isDisabled = isChatDisabled || isMessageLimited

  const isLogin = !!memberInfoId

  const isMaxLength = currentValue.length === MAX_MESSAGE_LENGTH

  const router = useRouter()

  const setUserRecordMutation = useMutation(setUserRecord)

  const handleInputChange = (event: any) => {
    setCurrentValue(event.target.value)
  }

  const handleInputSubmit = () => {
    if (isDisabled) return
    if (!currentValue) return

    if (isLogin) {
      if (isSlowing) {
        setCanSend(false)

        return
      }
      handleGtmLiveInteraction({
        interactionType: GtmLiveInteractionType.COMMENT,
        roomId,
        hostId,
      })
      handleChatSend({ socket, value: currentValue, role, name: nickName, headUrl: headshotUrl, isHost })

      setCurrentValue('')
      liveRoomContext.setIsSendBySelf(true)

      setIsSlowing(true)
      setIsBySelf(true)

      setTimeout(() => {
        setIsSlowing(false)
        setCanSend(true)
      }, SEND_BUFFER * 1000)
    } else {
      if (liveRoomContext.brandName === VALID_BRAND_NAME.PXEC) {
        liveRoomContext.settingUserNamePopupRef?.current?.setIsPopupOpen(true)
        return
      }
      loginPopupRef.current.setIsPopupOpen(true)
    }
  }

  const handlePhoneKeyDown = (event: any) => {
    const enterKeyCode = 13

    const isEnterKey = event.keyCode === enterKeyCode

    if (isEnterKey) handleInputSubmit()
  }

  const handleInputFocus = () => {
    // PXEC 品牌：比照其他品牌，只有 localStorage 沒有 token 時才跳出 nickname popup
    if (liveRoomContext.brandName === VALID_BRAND_NAME.PXEC) {
      const { authToken, settingUserNamePopupRef, isHost } = liveRoomContext

      // 如果是 host，不需要跳出 popup
      if (isHost) return

      // 只有當沒有 token 時才跳出 popup
      if (isEmpty(authToken) && settingUserNamePopupRef?.current) {
        settingUserNamePopupRef.current.setIsPopupOpen(true)
      }
    }
  }

  return (
    <div className={cx('chatInput', className)} data-is-disabled={isDisabled}>
      <input
        className={cx('chatInput-input')}
        value={isMessageLimited ? '' : currentValue}
        onChange={handleInputChange}
        onKeyDown={handlePhoneKeyDown}
        onFocus={handleInputFocus}
        placeholder={isMessageLimited ? t('LiveRoom_ChatInput_Placeholder_Limited') : t('LiveRoom_ChatInput_Placeholder')}
        data-is-disabled={isDisabled}
        disabled={isDisabled}
        maxLength={MAX_MESSAGE_LENGTH}
      />
      <div className={cx('chatInput-icon')} onClick={handleInputSubmit} data-is-disabled={isDisabled}>
        <Icon.Send />
      </div>

      {isMaxLength && <Alert text={t('LiveRoom_ChatInput_Alert_Count', { count: MAX_MESSAGE_LENGTH })} />}

      {!canSend && <Alert text={t('LiveRoom_ChatInput_Alert_Speed', { speedBuffer: SEND_BUFFER })} />}

      <Popup
        ref={loginPopupRef}
        title={t('LiveRoom_LoginPopup_Chat_Title')}
        confirmLabel={t('LiveRoom_LoginPopup_Confirm')}
        onConfirm={() => {
          handleLoginOrRegister({
            isLogin: true,
            router,
            setUserRecordMutation,
            fixedPlayerData,
            isGoRedirect: true,
            gtmLocation: GTM_LOCATION.LIVE_ROOM,
          })
        }}
        cancelLabel={t('LiveRoom_LoginPopup_Cancel')}
        onCancel={() => loginPopupRef.current.setIsPopupOpen(false)}
      >
        <p className={cx('chatInput-login')}>{t('LiveRoom_LoginPopup_Chat_Content')}</p>
      </Popup>
    </div>
  )
}

export default ChatInput
